import type { ReactNode } from 'react';

import type { TimelineFailure } from '../state/timeline.js';

/**
 * Empty, loading and error states (M1.16.1).
 *
 * Designed rather than defaulted, because these are most of what an alpha
 * tester sees in their first minute: a new account has no feeds, a new
 * subscription has no items until the feed publishes, and the backend is
 * sometimes not there at all.
 */

export function Loading({ label }: { label: string }): ReactNode {
  return (
    <div className="state" aria-live="polite">
      <div className="state__spinner" aria-hidden="true" />
      <p className="state__text">{label}</p>
    </div>
  );
}

/**
 * The empty states, which differ by *why* the list is empty.
 *
 * "No unread items" after triage is the product working — it is the bottom of
 * the list, and it should read as an achievement rather than as an absence. An
 * account with no feeds at all is a different message entirely, and showing
 * the triumphant one to somebody who has not subscribed to anything would be
 * absurd.
 */
export function Empty({
  reason,
  onAddFeed,
}: {
  reason: 'no-subscriptions' | 'nothing-unread' | 'nothing-here' | 'no-matches';
  onAddFeed: () => void;
}): ReactNode {
  if (reason === 'no-matches') {
    return (
      <div className="state">
        <h2 className="state__title">No matches</h2>
        <p className="state__text">
          Search looks for every word you typed in your own feeds — try fewer words, or
          another spelling.
        </p>
      </div>
    );
  }

  if (reason === 'no-subscriptions') {
    return (
      <div className="state">
        <h2 className="state__title">No feeds yet</h2>
        <p className="state__text">
          Paste a blog&rsquo;s address and we will find its feed — you do not need the
          feed URL itself.
        </p>
        <button className="button button--primary" onClick={onAddFeed}>
          Add a feed
        </button>
      </div>
    );
  }

  if (reason === 'nothing-unread') {
    return (
      <div className="state">
        <h2 className="state__title">You are at the bottom</h2>
        <p className="state__text">
          Nothing unread here. New items appear as your feeds publish them.
        </p>
      </div>
    );
  }

  return (
    <div className="state">
      <h2 className="state__title">Nothing here</h2>
      <p className="state__text">This feed has not published anything yet.</p>
    </div>
  );
}

/**
 * The error state, including the one that only exists because of D22.
 *
 * The web client lives in the persistent stack and the API in the disposable
 * one, so **the client is served perfectly well while its backend is gone** —
 * a deliberate consequence of the stack split, and the failure a tester is
 * most likely to meet at alpha. It says so, with what to do, rather than
 * spinning forever.
 */
export function Failure({
  failure,
  onRetry,
}: {
  failure: TimelineFailure;
  onRetry: () => void;
}): ReactNode {
  const offline = failure.kind === 'offline';

  return (
    <div className="state state--error" role="alert">
      <h2 className="state__title">
        {offline ? 'The service is not responding' : 'That did not work'}
      </h2>
      <p className="state__text">
        {offline
          ? 'Your feeds are safe. This alpha environment is sometimes rebuilt, which takes the API offline for a few minutes.'
          : failure.message}
      </p>
      {failure.kind === 'api' && failure.correlationId !== undefined && (
        <p className="state__meta">
          Reference <code>{failure.correlationId}</code> ·{' '}
          {/*
           * The problem-report form, with the code filled in (M5.7.2). The
           * code is what lets a report be matched to the failure without
           * the reader saying who they are.
           */}
          <a
            href={reportLink(failure.correlationId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Report this
          </a>
        </p>
      )}
      <button className="button" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/** The problem-report form, prefilled with a reference code (M5.7.1). */
export const reportLink = (reference: string): string =>
  `/surveys/report.html#ref=${encodeURIComponent(reference)}`;

/** The whole application failed to start — usually a missing `config.json`. */
export function FatalError({ detail }: { detail: string }): ReactNode {
  return (
    <main className="state state--fatal" role="alert">
      <h1 className="state__title">Feed Reader cannot start</h1>
      <p className="state__text">{detail}</p>
      <p className="state__meta">
        If this is the alpha environment, the backend is probably being rebuilt. Try again
        in a few minutes.
      </p>
    </main>
  );
}
